import { useState, useEffect } from "react";

// ─── CONFIGURATION ────────────────────────────────────────────────────────────
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const TRIAL_DAYS = 10; // broj dana besplatnog triala
// ─────────────────────────────────────────────────────────────────────────────

// Supabase client with auth support
const supabase = {
  _token: null,

  async query(table, method = "GET", body = null, filter = "") {
    const token = supabase._token || SUPABASE_ANON_KEY;
    const url = `${SUPABASE_URL}/rest/v1/${table}${filter}`;
    const res = await fetch(url, {
      method,
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Prefer: method === "POST" ? "return=representation" : "",
      },
      body: body ? JSON.stringify(body) : null,
    });
    if (!res.ok) throw new Error(await res.text());
    return method === "DELETE" ? null : res.json();
  },

  from: (table) => ({
    select: (filter = "") => supabase.query(table, "GET", null, `?select=*${filter}&order=created_at.desc`),
    insert: (data) => supabase.query(table, "POST", data),
    update: (data, filter) => supabase.query(table, "PATCH", data, filter),
    delete: (filter) => supabase.query(table, "DELETE", null, filter),
  }),

  auth: {
    async signUp(email, password) {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      return res.json();
    },
    async signIn(email, password) {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (data.access_token) {
        supabase._token = data.access_token;
        localStorage.setItem("sb_token", data.access_token);
        localStorage.setItem("sb_user", JSON.stringify({ id: data.user?.id, email: data.user?.email }));
      }
      return data;
    },
    signOut() {
      supabase._token = null;
      localStorage.removeItem("sb_token");
      localStorage.removeItem("sb_user");
    },
    async recover(email) {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/recover`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      return res.json();
    },
    async updatePassword(token, newPassword) {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        method: "PUT",
        headers: {
          apikey: SUPABASE_ANON_KEY,
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ password: newPassword }),
      });
      return res.json();
    },
    getSession() {
      const token = localStorage.getItem("sb_token");
      const user = localStorage.getItem("sb_user");
      if (token && user) {
        supabase._token = token;
        return { token, user: JSON.parse(user) };
      }
      return null;
    },
  },
};

// Claude AI call — goes through Vercel API route
async function askClaude(prompt) {
  try {
    const token = localStorage.getItem("sb_token") || "";
    const res = await fetch("/api/claude", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({
        model: "claude-sonnet-4-5",
        max_tokens: 1000,
        messages: [{ role: "user", content: prompt }],
      }),
    });
    const text = await res.text();
    const data = JSON.parse(text);
    return data.content?.[0]?.text || "No response.";
  } catch (err) {
    console.error("Claude error:", err);
    return "Could not get feedback.";
  }
}

// Claude vision — parse trade from screenshot
async function parseTradeFromImage(base64Data, mediaType) {
  try {
    const token = localStorage.getItem("sb_token") || "";
    const res = await fetch("/api/claude", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({
        model: "claude-sonnet-4-5",
        max_tokens: 500,
        messages: [{
          role: "user",
          content: [
            {
              type: "image",
              source: { type: "base64", media_type: mediaType, data: base64Data },
            },
            {
              type: "text",
              text: `You are a trading assistant. Extract trade details from this screenshot.
Return ONLY a valid JSON object with these fields (use empty string if not visible):
{
  "asset": "e.g. BTC/USDT or EURUSD",
  "direction": "Long or Short",
  "entry_price": "number only",
  "target_price": "number only",
  "stop_loss": "number only"
}
Do not include any explanation, only the JSON object.`,
            },
          ],
        }],
      }),
    });
    const text = await res.text();
    const data = JSON.parse(text);
    const content = data.content?.[0]?.text || "";
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return null;
    return JSON.parse(jsonMatch[0]);
  } catch (err) {
    console.error("Image parse error:", err);
    return null;
  }
}

// Claude text — parse trade from pasted text
async function parseTradeFromText(pastedText) {
  try {
    const token = localStorage.getItem("sb_token") || "";
    const res = await fetch("/api/claude", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({
        model: "claude-sonnet-4-5",
        max_tokens: 300,
        messages: [{
          role: "user",
          content: `You are a trading assistant. Extract trade details from this text.
Return ONLY a valid JSON object with these fields (use empty string if not found):
{
  "asset": "e.g. BTC/USDT or EURUSD or SPY",
  "direction": "Long or Short",
  "entry_price": "number only",
  "target_price": "number only",
  "stop_loss": "number only",
  "position_size": "number only"
}
Do not include any explanation, only the JSON object.

Text: ${pastedText}`,
        }],
      }),
    });
    const text = await res.text();
    const data = JSON.parse(text);
    const content = data.content?.[0]?.text || "";
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return null;
    return JSON.parse(jsonMatch[0]);
  } catch (err) {
    console.error("Text parse error:", err);
    return null;
  }
}

// ─── STYLES ──────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Mono:ital,wght@0,400;0,700;1,400&family=Syne:wght@400;500;600;700&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #080b0f;
    --bg2: #0e1318;
    --bg3: #141c24;
    --border: #1e2a35;
    --border2: #243040;
    --text: #e8edf2;
    --text2: #7a8fa0;
    --text3: #3d5060;
    --green: #00e5a0;
    --green-dim: #00e5a015;
    --red: #ff4060;
    --red-dim: #ff406015;
    --amber: #f5a623;
    --amber-dim: #f5a62315;
    --blue: #4090ff;
    --blue-dim: #4090ff12;
  }

  body { background: var(--bg); color: var(--text); font-family: 'Syne', sans-serif; min-height: 100vh; }

  .app { max-width: 520px; margin: 0 auto; padding: 0 0 80px; min-height: 100vh; }

  .header { padding: 28px 20px 0; display: flex; align-items: center; justify-content: space-between; margin-bottom: 28px; }
  .logo { font-family: 'Space Mono', monospace; font-size: 13px; color: var(--green); letter-spacing: 0.12em; }
  .header-actions { display: flex; gap: 8px; }
  .tab-btn { background: none; border: 1px solid var(--border2); color: var(--text2); font-family: 'Syne', sans-serif; font-size: 12px; padding: 6px 14px; border-radius: 20px; cursor: pointer; transition: all 0.15s; }
  .tab-btn.active, .tab-btn:hover { border-color: var(--green); color: var(--green); }

  .card { background: var(--bg2); border: 1px solid var(--border); border-radius: 16px; padding: 20px; margin: 0 16px 16px; }
  .card-sm { background: var(--bg3); border: 1px solid var(--border); border-radius: 12px; padding: 14px 16px; }

  .metrics { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; margin: 0 16px 16px; }
  .metric { background: var(--bg2); border: 1px solid var(--border); border-radius: 14px; padding: 18px 14px; }
  .metric-label { font-size: 12px; color: var(--text2); letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 10px; font-family: 'Syne', sans-serif; font-weight: 600; }
  .metric-value { font-size: 28px; font-weight: 700; line-height: 1; font-family: 'Space Mono', monospace; }
  .metric-value.green { color: var(--green); }
  .metric-value.amber { color: var(--amber); }
  .metric-value.neutral { color: var(--text); }

  .cta { display: flex; align-items: center; justify-content: center; gap: 10px; width: calc(100% - 32px); margin: 0 16px 20px; padding: 16px; background: var(--green-dim); border: 1px solid var(--green); border-radius: 14px; color: var(--green); font-family: 'Syne', sans-serif; font-weight: 600; font-size: 15px; cursor: pointer; transition: all 0.15s; }
  .cta:hover { background: #00e5a022; }

  .section-label { font-family: 'Syne', sans-serif; font-size: 12px; font-weight: 600; color: var(--text2); letter-spacing: 0.04em; text-transform: uppercase; padding: 0 20px; margin-bottom: 10px; }

  .trade-list { display: flex; flex-direction: column; gap: 8px; padding: 0 16px; }
  .trade-row { background: var(--bg2); border: 1px solid var(--border); border-radius: 12px; padding: 14px 16px; display: flex; align-items: center; gap: 12px; cursor: pointer; transition: border-color 0.15s; }
  .trade-row:hover { border-color: var(--border2); }
  .dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
  .dot.green { background: var(--green); box-shadow: 0 0 6px var(--green); }
  .dot.red { background: var(--red); box-shadow: 0 0 6px var(--red); }
  .dot.amber { background: var(--amber); box-shadow: 0 0 6px var(--amber); }
  .dot.open { background: var(--blue); box-shadow: 0 0 6px var(--blue); }
  .trade-asset { font-weight: 700; font-size: 13px; min-width: 80px; font-family: 'Space Mono', monospace; }
  .trade-date { font-size: 12px; color: var(--text2); flex: 1; }
  .trade-pnl { font-family: 'Space Mono', monospace; font-size: 13px; font-weight: 700; }
  .trade-pnl.pos { color: var(--green); }
  .trade-pnl.neg { color: var(--red); }
  .badge { font-size: 10px; padding: 3px 8px; border-radius: 20px; font-family: 'Space Mono', monospace; }
  .badge.yes { background: var(--green-dim); color: var(--green); border: 1px solid #00e5a030; }
  .badge.no { background: var(--red-dim); color: var(--red); border: 1px solid #ff406030; }
  .badge.partial { background: var(--amber-dim); color: var(--amber); border: 1px solid #f5a62330; }
  .badge.open { background: var(--blue-dim); color: var(--blue); border: 1px solid #4090ff30; }

  .form { display: flex; flex-direction: column; gap: 14px; }
  .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .field { display: flex; flex-direction: column; gap: 6px; }
  .field.full { grid-column: 1 / -1; }
  .field label { font-family: 'Syne', sans-serif; font-size: 12px; font-weight: 600; color: var(--text2); letter-spacing: 0.04em; text-transform: uppercase; }
  .field input, .field textarea, .field select {
    background: var(--bg3); border: 1px solid var(--border2); border-radius: 10px;
    padding: 12px 14px; color: var(--text); font-family: 'Syne', sans-serif; font-size: 16px;
    outline: none; transition: border-color 0.15s; width: 100%;
  }
  .field input::placeholder, .field textarea::placeholder { color: var(--text3); opacity: 1; font-size: 15px; }
  .field input:focus, .field textarea:focus, .field select:focus { border-color: var(--green); }
  .field textarea { resize: none; min-height: 80px; line-height: 1.5; }
  .field select option { background: var(--bg3); }

  .claude-box { background: var(--blue-dim); border: 1px solid #4090ff25; border-radius: 12px; padding: 14px 16px; }
  .claude-label { font-family: 'Syne', sans-serif; font-size: 12px; font-weight: 600; color: var(--blue); letter-spacing: 0.04em; text-transform: uppercase; margin-bottom: 8px; }
  .claude-text { font-size: 15px; color: #8ab4f8; line-height: 1.7; }

  .btn { padding: 12px 20px; border-radius: 12px; font-family: 'Syne', sans-serif; font-weight: 600; font-size: 14px; cursor: pointer; transition: all 0.15s; border: none; }
  .btn-primary { background: var(--green); color: #000; }
  .btn-primary:hover { opacity: 0.9; }
  .btn-secondary { background: var(--bg3); border: 1px solid var(--border2); color: var(--text2); }
  .btn-secondary:hover { border-color: var(--text2); color: var(--text); }
  .btn-row { display: flex; gap: 10px; }
  .btn-row .btn { flex: 1; }

  .review-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 0 16px 16px; }
  .review-card { background: var(--bg2); border: 1px solid var(--border); border-radius: 14px; padding: 16px; }
  .review-card-label { font-family: 'Syne', sans-serif; font-size: 12px; font-weight: 600; color: var(--text2); letter-spacing: 0.04em; text-transform: uppercase; margin-bottom: 12px; }
  .stat-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
  .stat-name { font-size: 12px; color: var(--text2); }
  .stat-val { font-family: 'Space Mono', monospace; font-size: 13px; font-weight: 700; }
  .bar-bg { height: 4px; background: var(--border); border-radius: 2px; margin-top: 2px; margin-bottom: 10px; }
  .bar-fill { height: 4px; border-radius: 2px; transition: width 0.6s ease; }

  .detail-field { margin-bottom: 14px; }
  .detail-label { font-family: 'Syne', sans-serif; font-size: 12px; font-weight: 600; color: var(--text2); letter-spacing: 0.04em; text-transform: uppercase; margin-bottom: 6px; }
  .detail-value { font-size: 16px; color: var(--text); line-height: 1.5; }
  .detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 14px; }

  .loading { display: flex; align-items: center; gap: 8px; color: var(--text2); font-size: 13px; padding: 12px 0; }
  .spinner { width: 16px; height: 16px; border: 2px solid var(--border2); border-top-color: var(--green); border-radius: 50%; animation: spin 0.7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }

  .empty { text-align: center; padding: 40px 20px; color: var(--text3); font-size: 13px; line-height: 1.8; }

  .back-btn { display: flex; align-items: center; gap: 8px; background: none; border: none; color: var(--text2); font-family: 'Syne', sans-serif; font-size: 14px; cursor: pointer; padding: 0 20px; margin-bottom: 20px; }
  .back-btn:hover { color: var(--text); }

  .page-title { font-size: 22px; font-weight: 700; padding: 0 20px; margin-bottom: 20px; }

  .divider { height: 1px; background: var(--border); margin: 16px 0; }

  .img-upload-btn { display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; padding: 12px; background: var(--bg3); border: 1px dashed var(--border2); border-radius: 12px; color: var(--text2); font-family: 'Syne', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s; }
  .img-upload-btn:hover { border-color: var(--green); color: var(--green); }
  .img-upload-btn.parsing { border-color: var(--blue); color: var(--blue); }
  .img-preview { width: 100%; border-radius: 10px; border: 1px solid var(--border); max-height: 160px; object-fit: cover; margin-top: 8px; }
`;

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const fmt = (n) => n > 0 ? `+$${n.toFixed(0)}` : n < 0 ? `-$${Math.abs(n).toFixed(0)}` : "$0";
const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const planLabel = (v) => v === "yes" ? "Followed" : v === "no" ? "Deviated" : v === "open" ? "Open" : "Partial";

// ─── STRATEGIES ──────────────────────────────────────────────────────────────
const STRATEGIES = {
  "": { label: "— Select strategy —", thesis: "", exit_conditions: "" },
  "trend_following": {
    label: "Trend Following",
    thesis: "Asset is in a clear uptrend/downtrend on [timeframe]. Price has pulled back to [EMA/level] and showing signs of continuation. Entering in the direction of the trend.",
    exit_conditions: "SL below the last swing low (long) or above the last swing high (short). Target next major resistance/support or trail stop as trend continues. Exit if price closes against the trend structure.",
  },
  "swing_trade": {
    label: "Swing Trade",
    thesis: "Identified a multi-day setup on [asset]. [Catalyst/pattern] forming on the [daily/4H] chart. Expecting a move over the next [X] days as [reason].",
    exit_conditions: "SL beyond the key level that invalidates the setup. Target based on next S/R or measured move. Will reassess if fundamental picture changes.",
  },
  "breakout": {
    label: "Breakout",
    thesis: "Price has been consolidating near [level] for [X] periods. Volume building. Expecting a confirmed breakout above/below [level] with momentum continuation.",
    exit_conditions: "SL just inside the consolidation range — if price returns inside, setup is invalid. Target 1.5–2x the range size. Exit immediately on failed breakout (close back inside range).",
  },
  "custom": {
    label: "Custom setup",
    thesis: "",
    exit_conditions: "",
  },
};

// ─── PAYWALL SCREEN ──────────────────────────────────────────────────────────
function PaywallScreen({ user, atLimit, onSubscribed, onBack }) {
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);

  async function goToCheckout() {
    setLoading(true);
    try {
      const res = await fetch("/api/lemon-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.id, email: user.email }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert("Could not create checkout. Try again.");
      }
    } catch (err) {
      alert("Error: " + err.message);
    }
    setLoading(false);
  }

  async function checkSubscription() {
    setChecking(true);
    const token = localStorage.getItem("sb_token");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/profiles?id=eq.${user.id}&select=subscription_status`,
        {
          headers: {
            apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await res.json();
      if (data?.[0]?.subscription_status === "active") {
        onSubscribed();
      } else {
        alert("Subscription not found yet. If you just paid, wait a moment and try again.");
      }
    } catch (err) {
      alert("Error checking subscription.");
    }
    setChecking(false);
  }

  return (
    <>
      <style>{css}</style>
      <div className="app">
        <div className="header">
          <div className="logo">TRADE//LOG</div>
          {onBack && <button className="tab-btn" onClick={onBack}>← Back</button>}
        </div>

        <div style={{ padding: "32px 16px 0" }}>

          {/* Naslov */}
          <div style={{ marginBottom: 28, padding: "0 4px" }}>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "var(--text3)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 10 }}>
              Upgrade
            </div>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 26, fontWeight: 700, color: "var(--text)", lineHeight: 1.25, marginBottom: 10 }}>
              {atLimit ? <>Your free trial<br />has ended</> : <>Unlock the<br />full journal</>}
            </div>
            <div style={{ fontSize: 15, color: "var(--text2)", lineHeight: 1.7 }}>
              {atLimit
                ? "Your 10-day free trial is over. Upgrade to Pro to keep logging trades and getting AI feedback."
                : "Here's everything you get with Trade Journal Pro."}
            </div>
          </div>

          {/* Kartica s cijenom */}
          <div className="card" style={{ marginBottom: 12, marginLeft: 0, marginRight: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
              <div>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 15, fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>
                  TRADE//LOG PRO
                </div>
                <div style={{ fontSize: 13, color: "var(--text3)" }}>Monthly subscription</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 28, fontWeight: 700, color: "var(--green)", lineHeight: 1 }}>
                  $5
                </div>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "var(--text3)" }}>/month</div>
              </div>
            </div>

            <div style={{ height: 1, background: "var(--border)", marginBottom: 18 }} />

            {[
              ["Unlimited trade logging", "Log as many trades as you want"],
              ["AI feedback on every entry", "Claude analyses each trade before you submit"],
              ["Pre-trade challenge questions", "Stay disciplined with guided reflection"],
              ["Pattern analysis", "Spot what's working across your journal"],
            ].map(([title, desc]) => (
              <div key={title} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 14 }}>
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 13, color: "var(--green)", marginTop: 1, flexShrink: 0 }}>✓</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)", marginBottom: 2 }}>{title}</div>
                  <div style={{ fontSize: 12, color: "var(--text3)", lineHeight: 1.5 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Dugmad */}
          <button className="cta" onClick={goToCheckout} disabled={loading} style={{ marginLeft: 0, marginRight: 0, width: "100%", marginBottom: 10 }}>
            {loading ? "Loading..." : "Upgrade to Pro — $5/month →"}
          </button>
          <button className="tab-btn" onClick={checkSubscription} disabled={checking} style={{ width: "100%", padding: "12px", fontSize: 13 }}>
            {checking ? "Checking..." : "I already paid — check my subscription"}
          </button>

        </div>
      </div>
    </>
  );
}

// ─── AUTH SCREEN ─────────────────────────────────────────────────────────────
function AuthScreen({ onAuth, initialMode }) {
  const [mode, setMode] = useState(initialMode || "login"); // "login" | "signup" | "forgot" | "reset"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  function go(m) { setMode(m); setError(""); setMsg(""); }

  async function submit() {
    setError(""); setMsg("");
    if (mode === "forgot") {
      if (!email) return setError("Enter your email.");
      setLoading(true);
      await supabase.auth.recover(email);
      setMsg("If that email exists, a reset link has been sent. Check your inbox.");
      setLoading(false);
      return;
    }
    if (mode === "reset") {
      if (!password || !password2) return setError("Enter and confirm your new password.");
      if (password !== password2) return setError("Passwords don't match.");
      setLoading(true);
      const token = new URLSearchParams(window.location.hash.slice(1)).get("access_token");
      const data = await supabase.auth.updatePassword(token, password);
      if (data.error) {
        setError(data.error.message || "Reset failed.");
      } else {
        setMsg("Password updated! You can now log in.");
        window.history.replaceState({}, "", "/");
        go("login");
      }
      setLoading(false);
      return;
    }
    if (!email || !password) return setError("Enter email and password.");
    setLoading(true);
    if (mode === "signup") {
      const data = await supabase.auth.signUp(email, password);
      if (data.error) {
        setError(data.error.message || "Signup failed.");
      } else {
        setMsg("Check your email to confirm your account, then log in.");
        go("login");
      }
    } else {
      const data = await supabase.auth.signIn(email, password);
      if (data.error) {
        setError(data.error.message || "Login failed.");
      } else if (data.access_token) {
        onAuth({ id: data.user?.id, email: data.user?.email });
      } else {
        setError("Login failed. Check your credentials.");
      }
    }
    setLoading(false);
  }

  const linkBtn = { background: "none", border: "none", color: "var(--green)", cursor: "pointer", fontFamily: "inherit", fontSize: 16 };

  return (
    <>
      <style>{css}</style>
      <div className="app">
        <div className="header">
          <div className="logo">TRADE//LOG</div>
          {(mode === "forgot" || mode === "reset") && (
            <button className="tab-btn" onClick={() => go("login")}>← Back</button>
          )}
        </div>
        <div style={{ padding: "32px 16px 0" }}>

          {/* Hero — samo na login i signup */}
          {(mode === "login" || mode === "signup") && (
            <div style={{ marginBottom: 32 }}>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 22, fontWeight: 700, color: "var(--text)", lineHeight: 1.3, marginBottom: 12 }}>
                Stop trading on impulse.<br />Start trading with a plan.
              </div>
              <div style={{ fontSize: 15, color: "var(--text2)", lineHeight: 1.7, marginBottom: 24 }}>
                AI-powered trade journal that challenges your thesis before every entry — so you catch bad trades before they cost you.
              </div>
              {[
                ["⚡", "AI challenges your thesis before you click buy"],
                ["📋", "Log your plan, review your discipline"],
                ["📈", "Spot patterns across your trading history"],
              ].map(([icon, text]) => (
                <div key={text} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10, fontSize: 14, color: "var(--text2)" }}>
                  <span style={{ fontSize: 16, lineHeight: 1.4 }}>{icon}</span>
                  <span>{text}</span>
                </div>
              ))}
              <div style={{ marginTop: 16, fontSize: 13, color: "var(--green)", fontFamily: "'Space Mono', monospace" }}>
                Free for {TRIAL_DAYS} days. No credit card needed.
              </div>
            </div>
          )}

          <div className="page-title" style={{ fontSize: 18, marginBottom: 8 }}>
            {mode === "login" && "Welcome back"}
            {mode === "signup" && "Create account"}
            {mode === "forgot" && "Reset password"}
            {mode === "reset" && "Set new password"}
          </div>
          <p style={{ color: "var(--text2)", fontSize: 13, marginBottom: 28 }}>
            {mode === "login" && "Log in to your journal"}
            {mode === "signup" && "Start tracking your trades"}
            {mode === "forgot" && "We'll send a reset link to your email"}
            {mode === "reset" && "Choose a new password for your account"}
          </p>
          <div className="card" style={{ margin: 0 }}>
            <div className="form">
              {(mode === "login" || mode === "signup" || mode === "forgot") && (
                <div className="field full">
                  <label>Email</label>
                  <input type="email" placeholder="you@example.com" value={email}
                    onChange={e => setEmail(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && submit()} />
                </div>
              )}
              {(mode === "login" || mode === "signup") && (
                <div className="field full">
                  <label>Password</label>
                  <input type="password" placeholder="••••••••" value={password}
                    onChange={e => setPassword(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && submit()} />
                </div>
              )}
              {mode === "reset" && (<>
                <div className="field full">
                  <label>New password</label>
                  <input type="password" placeholder="••••••••" value={password}
                    onChange={e => setPassword(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && submit()} />
                </div>
                <div className="field full">
                  <label>Confirm password</label>
                  <input type="password" placeholder="••••••••" value={password2}
                    onChange={e => setPassword2(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && submit()} />
                </div>
              </>)}
              {error && <div style={{ color: "var(--red)", fontSize: 13 }}>{error}</div>}
              {msg && <div style={{ color: "var(--green)", fontSize: 13 }}>{msg}</div>}
              <button className="btn btn-primary" style={{ width: "100%" }} onClick={submit} disabled={loading}>
                {loading ? "Loading..." : mode === "login" ? "Log in" : mode === "signup" ? "Sign up" : mode === "forgot" ? "Send reset link" : "Set new password"}
              </button>
            </div>
          </div>
          <div style={{ textAlign: "center", marginTop: 24, color: "var(--text2)", fontSize: 16, display: "flex", flexDirection: "column", gap: 14 }}>
            {mode === "login" && (<>
              <span>No account? <button onClick={() => go("signup")} style={linkBtn}>Sign up</button></span>
              <span><button onClick={() => go("forgot")} style={{ ...linkBtn, color: "var(--text2)" }}>Forgot password?</button></span>
            </>)}
            {mode === "signup" && (
              <span>Already have one? <button onClick={() => go("login")} style={linkBtn}>Log in</button></span>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ─── MAIN APP ────────────────────────────────────────────────────────────────
export default function TradeJournal() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [screen, setScreen] = useState("dashboard");
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    // Detektuj password reset link iz emaila (#access_token=...&type=recovery)
    const hash = new URLSearchParams(window.location.hash.slice(1));
    if (hash.get("type") === "recovery") {
      setAuthChecked(true);
      return; // AuthScreen će prikazati reset formu
    }
    const session = supabase.auth.getSession();
    if (session) setUser(session.user);
    setAuthChecked(true);
  }, []);

  useEffect(() => {
    if (user) {
      checkSubscription();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  async function checkSubscription() {
    const params = new URLSearchParams(window.location.search);
    if (params.get("subscribed") === "true") {
      window.history.replaceState({}, "", "/");
    }
    try {
      const token = localStorage.getItem("sb_token");

      // Provjeri subscription status
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/profiles?id=eq.${user.id}&select=subscription_status`,
        { headers: { apikey: process.env.REACT_APP_SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      const isPaid = data?.[0]?.subscription_status === "active";
      setSubscribed(isPaid);

      // Dohvati datum registracije
      const userRes = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/auth/v1/user`, {
        headers: { apikey: process.env.REACT_APP_SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` }
      });
      const userData = await userRes.json();
      const createdAt = new Date(userData.created_at);
      const daysSince = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24);
      const daysLeft = Math.max(0, Math.ceil(TRIAL_DAYS - daysSince));
      setTrialDaysLeft(daysLeft);
    } catch (e) {
      console.error("Sub check error:", e);
    }
    loadTrades();
  }

  async function loadTrades() {
    try {
      setLoading(true);
      const data = await supabase.from("trades").select();
      setTrades(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  function handleSignOut() {
    supabase.auth.signOut();
    setUser(null);
    setTrades([]);
    setScreen("dashboard");
  }

  if (!authChecked) return null;
  if (!user) {
    const isReset = new URLSearchParams(window.location.hash.slice(1)).get("type") === "recovery";
    return <AuthScreen onAuth={(u) => setUser(u)} initialMode={isReset ? "reset" : "login"} />;
  }

  const inTrial = trialDaysLeft === null || trialDaysLeft > 0;
  const atLimit = !subscribed && !inTrial;

  const closedTrades = trades.filter(t => t.status === "closed");
  const openTrades = trades.filter(t => t.status === "open");
  const adherenceRate = closedTrades.length
    ? Math.round((closedTrades.filter(t => t.followed_plan === "yes").length / closedTrades.length) * 100)
    : null;
  const winRate = closedTrades.length
    ? Math.round((closedTrades.filter(t => (t.pnl || 0) > 0).length / closedTrades.length) * 100)
    : null;

  return (
    <>
      <style>{css}</style>
      <div className="app">
        <div className="header">
          <div className="logo">TRADE//LOG</div>
          <div className="header-actions">
            <button className={`tab-btn ${screen === "dashboard" ? "active" : ""}`} onClick={() => setScreen("dashboard")}>Journal</button>
            <button className={`tab-btn ${screen === "review" ? "active" : ""}`} onClick={() => setScreen("review")}>Review</button>
            {!subscribed && (
              <span
                onClick={() => setScreen("paywall")}
                title="See what's included in Pro"
                style={{ fontSize: 12, color: atLimit ? "var(--red, #ff4d4d)" : "var(--text2)", cursor: "pointer", padding: "4px 8px", border: "1px solid currentColor", borderRadius: 4 }}
              >
                {atLimit ? "Trial ended" : trialDaysLeft === 1 ? "1 day left" : `${trialDaysLeft ?? "..."} days left`}
              </span>
            )}
            <button className="tab-btn" onClick={handleSignOut} title={user.email}>Sign out</button>
          </div>
        </div>

        {screen === "dashboard" && (
          <Dashboard
            trades={trades} openTrades={openTrades} closedTrades={closedTrades}
            adherenceRate={adherenceRate} winRate={winRate} loading={loading}
            onNew={() => atLimit ? setScreen("paywall") : setScreen("log")}
            onSelect={(t) => { setSelected(t); setScreen("detail"); }}
          />
        )}
        {screen === "log" && (
          <LogTrade userId={user.id} onSave={async () => { await loadTrades(); setScreen("dashboard"); }} onBack={() => setScreen("dashboard")} />
        )}
        {screen === "paywall" && (
          <PaywallScreen user={user} atLimit={atLimit} onSubscribed={() => { setSubscribed(true); setScreen("log"); }} onBack={() => setScreen("dashboard")} />
        )}
        {screen === "detail" && selected && (
          <TradeDetail trade={selected} onBack={() => { setSelected(null); setScreen("dashboard"); }} onClose={async () => { await loadTrades(); setSelected(null); setScreen("dashboard"); }} />
        )}
        {screen === "review" && (
          <Review trades={closedTrades} />
        )}
      </div>
    </>
  );
}

// ─── DASHBOARD ───────────────────────────────────────────────────────────────
function Dashboard({ trades, openTrades, closedTrades, adherenceRate, winRate, loading, onNew, onSelect }) {
  return (
    <>
      <div className="metrics">
        <div className="metric">
          <div className="metric-label">Adherence</div>
          <div className={`metric-value ${adherenceRate >= 70 ? "green" : adherenceRate >= 50 ? "amber" : "neutral"}`}>
            {adherenceRate !== null ? `${adherenceRate}%` : "—"}
          </div>
        </div>
        <div className="metric">
          <div className="metric-label">Trades</div>
          <div className="metric-value neutral">{closedTrades.length}</div>
        </div>
        <div className="metric">
          <div className="metric-label">Win rate</div>
          <div className={`metric-value ${winRate >= 55 ? "green" : winRate >= 40 ? "amber" : "neutral"}`}>
            {winRate !== null ? `${winRate}%` : "—"}
          </div>
        </div>
      </div>

      <button className="cta" onClick={onNew}>
        <span style={{ fontSize: 18 }}>+</span> Log new trade
      </button>

      {openTrades.length > 0 && (
        <>
          <div className="section-label">Open positions</div>
          <div className="trade-list" style={{ marginBottom: 16 }}>
            {openTrades.map(t => (
              <div key={t.id} className="trade-row" onClick={() => onSelect(t)}>
                <div className="dot open" />
                <div className="trade-asset">{t.asset}</div>
                <div className="trade-date">{fmtDate(t.created_at)} · {t.direction}</div>
                <div className="badge open">Open</div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="section-label">Recent trades</div>
      <div className="trade-list">
        {loading && <div className="loading"><div className="spinner" /> Loading...</div>}
        {!loading && trades.length === 0 && (
          <div className="empty">No trades yet.<br />Log your first trade to get started.</div>
        )}
        {!loading && closedTrades.map(t => (
          <div key={t.id} className="trade-row" onClick={() => onSelect(t)}>
            <div className={`dot ${(t.pnl || 0) > 0 ? "green" : (t.pnl || 0) < 0 ? "red" : "amber"}`} />
            <div className="trade-asset">{t.asset}</div>
            <div className="trade-date">{fmtDate(t.created_at)}</div>
            <div className={`badge ${t.followed_plan}`}>{planLabel(t.followed_plan)}</div>
            <div className={`trade-pnl ${(t.pnl || 0) >= 0 ? "pos" : "neg"}`}>{fmt(t.pnl || 0)}</div>
          </div>
        ))}
      </div>
    </>
  );
}

// ─── LOG TRADE ────────────────────────────────────────────────────────────────
function LogTrade({ userId, onSave, onBack }) {
  const [form, setForm] = useState({ asset: "", direction: "Long", entry_price: "", target_price: "", stop_loss: "", position_size: "", thesis: "", exit_conditions: "" });
  const [strategy, setStrategy] = useState("");
  const [claudeQ, setClaudeQ] = useState("");
  const [loadingQ, setLoadingQ] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customStrategies, setCustomStrategies] = useState([]);
  const [showNewStrat, setShowNewStrat] = useState(false);
  const [newStrat, setNewStrat] = useState({ name: "", thesis: "", exit_conditions: "" });
  const [savingStrat, setSavingStrat] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [parseMsg, setParseMsg] = useState("");
  const [pasteText, setPasteText] = useState("");
  const [showPaste, setShowPaste] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleImageUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const mediaType = file.type || "image/jpeg";
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target.result;
      const base64 = dataUrl.split(",")[1];
      setImagePreview(dataUrl);
      setParsing(true);
      setParseMsg("Reading your screenshot...");
      const parsed = await parseTradeFromImage(base64, mediaType);
      setParsing(false);
      if (!parsed) {
        setParseMsg("Couldn't read trade data. Fill in manually.");
        return;
      }
      setForm(f => ({
        ...f,
        asset: parsed.asset || f.asset,
        direction: parsed.direction === "Short" ? "Short" : "Long",
        entry_price: parsed.entry_price || f.entry_price,
        target_price: parsed.target_price || f.target_price,
        stop_loss: parsed.stop_loss || f.stop_loss,
      }));
      setParseMsg("✓ Fields filled from screenshot — check and adjust if needed.");
    };
    reader.readAsDataURL(file);
  }

  async function handlePasteParse() {
    if (!pasteText.trim()) return;
    setParsing(true);
    setParseMsg("Reading your text...");
    const parsed = await parseTradeFromText(pasteText);
    setParsing(false);
    if (!parsed) {
      setParseMsg("Couldn't read trade data. Fill in manually.");
      return;
    }
    setForm(f => ({
      ...f,
      asset: parsed.asset || f.asset,
      direction: parsed.direction === "Short" ? "Short" : "Long",
      entry_price: parsed.entry_price || f.entry_price,
      target_price: parsed.target_price || f.target_price,
      stop_loss: parsed.stop_loss || f.stop_loss,
      position_size: parsed.position_size || f.position_size,
    }));
    setParseMsg("✓ Fields filled — check and adjust if needed.");
    setPasteText("");
    setShowPaste(false);
    if (parsed.target_price || parsed.stop_loss || parsed.position_size) {
      setShowAdvanced(true);
    }
  }

  useEffect(() => {
    supabase.from("strategies").select(`&user_id=eq.${userId}`)
      .then(data => setCustomStrategies(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [userId]);

  function selectStrategy(key) {
    if (key === "__new__") {
      setShowNewStrat(true);
      return;
    }
    setStrategy(key);
    setClaudeQ("");
    setShowNewStrat(false);
    if (key && STRATEGIES[key] && key !== "custom") {
      setForm(f => ({ ...f, thesis: STRATEGIES[key].thesis, exit_conditions: STRATEGIES[key].exit_conditions }));
    } else if (key === "custom") {
      setForm(f => ({ ...f, thesis: "", exit_conditions: "" }));
    } else {
      // custom strategy from Supabase
      const cs = customStrategies.find(s => s.id === key);
      if (cs) setForm(f => ({ ...f, thesis: cs.thesis || "", exit_conditions: cs.exit_conditions || "" }));
    }
  }

  async function saveNewStrategy() {
    if (!newStrat.name.trim()) return alert("Enter a strategy name.");
    setSavingStrat(true);
    try {
      const data = await supabase.from("strategies").insert({ user_id: userId, name: newStrat.name.trim(), thesis: newStrat.thesis, exit_conditions: newStrat.exit_conditions });
      const saved = Array.isArray(data) ? data[0] : data;
      setCustomStrategies(prev => [...prev, saved]);
      setStrategy(saved.id);
      setForm(f => ({ ...f, thesis: saved.thesis || "", exit_conditions: saved.exit_conditions || "" }));
      setNewStrat({ name: "", thesis: "", exit_conditions: "" });
      setShowNewStrat(false);
    } catch (e) { alert("Error saving strategy."); }
    setSavingStrat(false);
  }

  async function deleteStrategy(id, e) {
    e.stopPropagation();
    if (!window.confirm("Delete this strategy?")) return;
    await supabase.from("strategies").delete(`?id=eq.${id}`);
    setCustomStrategies(prev => prev.filter(s => s.id !== id));
    if (strategy === id) { setStrategy(""); setForm(f => ({ ...f, thesis: "", exit_conditions: "" })); }
  }

  async function generateQuestion() {
    if (!form.thesis) return;
    setLoadingQ(true);
    const q = await askClaude(`You are a sharp trading coach. A trader is about to enter this trade:
Asset: ${form.asset}, Direction: ${form.direction}, Entry: ${form.entry_price}, Target: ${form.target_price}, Stop: ${form.stop_loss}
Strategy: ${STRATEGIES[strategy]?.label || customStrategies.find(s => s.id === strategy)?.name || "Custom"}
Thesis: ${form.thesis}
Exit conditions: ${form.exit_conditions || "not specified"}

Ask ONE sharp, specific question that challenges their reasoning or exposes a gap in their plan. Be direct. Max 2 sentences.`);
    setClaudeQ(q);
    setLoadingQ(false);
  }

  async function save() {
    if (!form.asset || !form.thesis) return alert("Fill in asset and thesis at minimum.");
    setSaving(true);
    try {
      await supabase.from("trades").insert({
        ...form,
        user_id: userId,
        entry_price: parseFloat(form.entry_price) || null,
        target_price: parseFloat(form.target_price) || null,
        stop_loss: parseFloat(form.stop_loss) || null,
        position_size: parseFloat(form.position_size) || null,
        status: "open",
        followed_plan: "open",
        claude_pre_question: claudeQ,
        strategy: STRATEGIES[strategy]?.label || customStrategies.find(s => s.id === strategy)?.name || strategy,
      });
      onSave();
    } catch (e) { console.error("Error saving:", e.message); }
    setSaving(false);
  }

  return (
    <>
      <button className="back-btn" onClick={onBack}>← Back</button>
      <div className="page-title">Log trade</div>
      <div className="card">
        <div className="form">

          {/* Import sekcija — screenshot ili paste */}
          <div className="field full">
            <label>Auto-fill from</label>
            <div style={{ display: "flex", gap: 8 }}>
              <label className={`img-upload-btn ${parsing ? "parsing" : ""}`} style={{ flex: 1 }}>
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageUpload} disabled={parsing} />
                {parsing ? <><div className="spinner" /> Reading...</> : <>📷 Screenshot</>}
              </label>
              <button
                type="button"
                className={`img-upload-btn ${showPaste ? "parsing" : ""}`}
                style={{ flex: 1 }}
                onClick={() => { setShowPaste(p => !p); setParseMsg(""); }}
                disabled={parsing}
              >
                📋 Paste text
              </button>
            </div>

            {/* Paste polje */}
            {showPaste && (
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                <textarea
                  style={{ background: "var(--bg3)", border: "1px solid var(--border2)", borderRadius: 10, padding: "10px 14px", color: "var(--text)", fontFamily: "'Syne', sans-serif", fontSize: 14, minHeight: 72, resize: "none", outline: "none", width: "100%" }}
                  placeholder={"Paste trade details, broker confirmation, or just type:\ne.g. BTC long 62400 tp 65000 sl 61000 size 500"}
                  value={pasteText}
                  onChange={e => setPasteText(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handlePasteParse(); }}
                />
                <button
                  className="btn btn-secondary"
                  style={{ fontSize: 13, padding: "8px 14px" }}
                  onClick={handlePasteParse}
                  disabled={parsing || !pasteText.trim()}
                >
                  {parsing ? <><div className="spinner" style={{ display: "inline-block" }} /> Reading...</> : "Fill fields →"}
                </button>
              </div>
            )}

            {imagePreview && <img src={imagePreview} alt="preview" className="img-preview" />}
            {parseMsg && <div style={{ fontSize: 12, color: parseMsg.startsWith("✓") ? "var(--green)" : "var(--text2)", marginTop: 6 }}>{parseMsg}</div>}
          </div>
          <div className="divider" />

          {/* Strategija */}
          <div className="field full">
            <label>Strategy</label>
            <select value={strategy} onChange={e => selectStrategy(e.target.value)}>
              {Object.entries(STRATEGIES).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
              {customStrategies.length > 0 && (
                <optgroup label="My strategies">
                  {customStrategies.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </optgroup>
              )}
              <option value="__new__">＋ Add my strategy</option>
            </select>
          </div>

          {/* Forma za novu strategiju */}
          {showNewStrat && (
            <div style={{ background: "var(--bg3)", border: "1px solid var(--border2)", borderRadius: 12, padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "var(--green)", letterSpacing: "0.1em", textTransform: "uppercase" }}>New strategy</div>
              <div className="field full">
                <label>Name</label>
                <input placeholder="e.g. London Breakout" value={newStrat.name} onChange={e => setNewStrat(s => ({ ...s, name: e.target.value }))} />
              </div>
              <div className="field full">
                <label>Thesis / Setup description</label>
                <textarea placeholder="Describe your entry conditions..." value={newStrat.thesis} onChange={e => setNewStrat(s => ({ ...s, thesis: e.target.value }))} />
              </div>
              <div className="field full">
                <label>Exit conditions</label>
                <textarea placeholder="SL, TP, when to exit early..." value={newStrat.exit_conditions} onChange={e => setNewStrat(s => ({ ...s, exit_conditions: e.target.value }))} />
              </div>
              <div className="btn-row">
                <button className="btn btn-primary" onClick={saveNewStrategy} disabled={savingStrat}>{savingStrat ? "Saving..." : "Save strategy"}</button>
                <button className="btn btn-secondary" onClick={() => { setShowNewStrat(false); setStrategy(""); }}>Cancel</button>
              </div>
            </div>
          )}

          {/* Brisanje custom strategije */}
          {strategy && !STRATEGIES[strategy] && customStrategies.find(s => s.id === strategy) && (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button onClick={e => deleteStrategy(strategy, e)} style={{ background: "none", border: "none", color: "var(--red)", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                Delete this strategy
              </button>
            </div>
          )}

          {/* Obavezna polja */}
          <div className="form-row">
            <div className="field">
              <label>Asset</label>
              <input placeholder="BTC/USDT" value={form.asset} onChange={e => set("asset", e.target.value)} />
            </div>
            <div className="field">
              <label>Direction</label>
              <select value={form.direction} onChange={e => set("direction", e.target.value)}>
                <option>Long</option>
                <option>Short</option>
              </select>
            </div>
          </div>
          <div className="field full">
            <label>Entry price</label>
            <input placeholder="62400" value={form.entry_price} onChange={e => set("entry_price", e.target.value)} />
          </div>
          <div className="field full">
            <label>Thesis</label>
            <textarea value={form.thesis} onChange={e => { set("thesis", e.target.value); setClaudeQ(""); }} placeholder="Why are you entering this trade?" />
          </div>

          {/* Advanced toggle */}
          <button
            type="button"
            onClick={() => setShowAdvanced(a => !a)}
            style={{ background: "none", border: "none", color: "var(--text2)", fontFamily: "'Syne', sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", padding: "4px 0", textAlign: "left", display: "flex", alignItems: "center", gap: 6 }}
          >
            <span style={{ fontSize: 11, transition: "transform 0.2s", display: "inline-block", transform: showAdvanced ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>
            {showAdvanced ? "Hide details" : "Add target, SL, position size…"}
          </button>

          {showAdvanced && (
            <>
              <div className="form-row">
                <div className="field">
                  <label>Target price</label>
                  <input placeholder="65000" value={form.target_price} onChange={e => set("target_price", e.target.value)} />
                </div>
                <div className="field">
                  <label>Stop loss</label>
                  <input placeholder="61000" value={form.stop_loss} onChange={e => set("stop_loss", e.target.value)} />
                </div>
              </div>
              <div className="field full">
                <label>Position size ($)</label>
                <input placeholder="500" value={form.position_size} onChange={e => set("position_size", e.target.value)} />
              </div>
              <div className="field full">
                <label>Exit conditions</label>
                <textarea value={form.exit_conditions} onChange={e => set("exit_conditions", e.target.value)} style={{ minHeight: 60 }} placeholder="When will you exit?" />
              </div>
            </>
          )}
        </div>

        <button
          className={`img-upload-btn ${loadingQ ? "parsing" : ""}`}
          style={{ margin: "0 0 4px" }}
          onClick={generateQuestion}
          disabled={loadingQ || !form.thesis}
        >
          {loadingQ
            ? <><div className="spinner" /> Claude is thinking...</>
            : claudeQ
            ? <>🔄 Ask Claude again</>
            : <>⚡ Challenge my thesis with Claude</>}
        </button>

        {claudeQ && (
          <>
            <div className="claude-box" style={{ marginBottom: 8 }}>
              <div className="claude-label">Claude asks</div>
              <div className="claude-text">{claudeQ}</div>
            </div>
          </>
        )}

        <div className="divider" />
        <div className="btn-row">
          <button className="btn btn-secondary" onClick={onBack}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Saving..." : "Log trade →"}</button>
        </div>
      </div>
    </>
  );
}

// ─── TRADE DETAIL / CLOSE ─────────────────────────────────────────────────────
function TradeDetail({ trade, onBack, onClose }) {
  const [closing, setClosing] = useState(false);
  const [closeForm, setCloseForm] = useState({ exit_price: "", followed_plan: "yes", what_happened: "" });
  const [claudeFeedback, setClaudeFeedback] = useState(trade.claude_feedback || "");
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setCloseForm(f => ({ ...f, [k]: v }));

  async function closeTrade() {
    if (!closeForm.exit_price) return alert("Enter exit price.");
    setSaving(true);
    setLoadingFeedback(true);

    const pnl = trade.position_size
      ? ((parseFloat(closeForm.exit_price) - trade.entry_price) / trade.entry_price) * trade.position_size * (trade.direction === "Short" ? -1 : 1)
      : 0;

    const feedback = await askClaude(`You are a blunt trading coach reviewing a closed trade. Be honest, specific, and concise. Max 3 sentences.

Trade: ${trade.asset} ${trade.direction}
Entry: $${trade.entry_price} → Exit: $${closeForm.exit_price}
Target was: $${trade.target_price}, Stop was: $${trade.stop_loss}
Position: $${trade.position_size}, P&L: ${fmt(pnl)}
Original thesis: ${trade.thesis}
Exit conditions planned: ${trade.exit_conditions || "none"}
Followed plan: ${closeForm.followed_plan}
What happened: ${closeForm.what_happened}
${trade.claude_pre_question ? `Pre-trade question asked: ${trade.claude_pre_question}` : ""}

Give feedback on what they did well or poorly, and one actionable takeaway.`);

    setLoadingFeedback(false);
    setClaudeFeedback(feedback);

    try {
      await supabase.from("trades").update({
        exit_price: parseFloat(closeForm.exit_price),
        followed_plan: closeForm.followed_plan,
        what_happened: closeForm.what_happened,
        pnl: Math.round(pnl),
        status: "closed",
        claude_feedback: feedback,
      }, `?id=eq.${trade.id}`);
      setSaving(false);
      onClose();
    } catch (e) {
      console.error("Save error:", e.message);
      alert("Error saving trade. Please try again.");
      setSaving(false);
    }
  }

  return (
    <>
      <button className="back-btn" onClick={onBack}>← Back</button>
      <div className="page-title">{trade.asset} · {trade.direction}</div>
      <div className="card">
        <div className="detail-grid">
          <div className="detail-field">
            <div className="detail-label">Entry price</div>
            <div className="detail-value">${trade.entry_price}</div>
          </div>
          <div className="detail-field">
            <div className="detail-label">Target</div>
            <div className="detail-value">${trade.target_price || "—"}</div>
          </div>
          <div className="detail-field">
            <div className="detail-label">Stop loss</div>
            <div className="detail-value">${trade.stop_loss || "—"}</div>
          </div>
          <div className="detail-field">
            <div className="detail-label">Position size</div>
            <div className="detail-value">${trade.position_size || "—"}</div>
          </div>
        </div>
        <div className="detail-field">
          <div className="detail-label">Thesis</div>
          <div className="detail-value">{trade.thesis}</div>
        </div>
        {trade.exit_conditions && (
          <div className="detail-field">
            <div className="detail-label">Exit conditions</div>
            <div className="detail-value">{trade.exit_conditions}</div>
          </div>
        )}
        {trade.claude_pre_question && (
          <>
            <div className="divider" />
            <div className="claude-box">
              <div className="claude-label">Claude's pre-trade question</div>
              <div className="claude-text">{trade.claude_pre_question}</div>
            </div>
          </>
        )}

        {trade.status === "closed" && (
          <>
            <div className="divider" />
            <div className="detail-grid">
              <div className="detail-field">
                <div className="detail-label">Exit price</div>
                <div className="detail-value">${trade.exit_price}</div>
              </div>
              <div className="detail-field">
                <div className="detail-label">P&L</div>
                <div className="detail-value" style={{ color: trade.pnl >= 0 ? "var(--green)" : "var(--red)", fontWeight: 700 }}>{fmt(trade.pnl)}</div>
              </div>
            </div>
            <div className="detail-field">
              <div className="detail-label">Followed plan</div>
              <div className="detail-value"><span className={`badge ${trade.followed_plan}`}>{planLabel(trade.followed_plan)}</span></div>
            </div>
            {trade.what_happened && (
              <div className="detail-field">
                <div className="detail-label">What happened</div>
                <div className="detail-value">{trade.what_happened}</div>
              </div>
            )}
            {trade.claude_feedback && (
              <>
                <div className="divider" />
                <div className="claude-box">
                  <div className="claude-label">Claude's feedback</div>
                  <div className="claude-text">{trade.claude_feedback}</div>
                </div>
              </>
            )}
          </>
        )}

        {trade.status === "open" && (
          <>
            <div className="divider" />
            {!closing ? (
              <button className="btn btn-primary" style={{ width: "100%" }} onClick={() => setClosing(true)}>Close this trade</button>
            ) : (
              <div className="form">
                <div className="field full">
                  <label>Actual exit price</label>
                  <input placeholder="63100" value={closeForm.exit_price} onChange={e => set("exit_price", e.target.value)} />
                </div>
                <div className="field full">
                  <label>Did you follow your plan?</label>
                  <select value={closeForm.followed_plan} onChange={e => set("followed_plan", e.target.value)}>
                    <option value="yes">Yes — followed the plan</option>
                    <option value="partial">Partially</option>
                    <option value="no">No — deviated</option>
                  </select>
                </div>
                <div className="field full">
                  <label>One honest sentence — what happened?</label>
                  <textarea placeholder="I saw red candles and panicked before target..." value={closeForm.what_happened} onChange={e => set("what_happened", e.target.value)} style={{ minHeight: 60 }} />
                </div>
                {(loadingFeedback || claudeFeedback) && (
                  <div className="claude-box">
                    <div className="claude-label">Claude's feedback</div>
                    {loadingFeedback ? <div className="loading"><div className="spinner" /> Analyzing...</div> : <div className="claude-text">{claudeFeedback}</div>}
                  </div>
                )}
                <div className="btn-row">
                  <button className="btn btn-secondary" onClick={() => setClosing(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={closeTrade} disabled={saving}>{saving ? "Saving..." : "Close trade"}</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

// ─── REVIEW ───────────────────────────────────────────────────────────────────
function Review({ trades }) {
  const [analysis, setAnalysis] = useState("");
  const [loading, setLoading] = useState(false);

  const followed = trades.filter(t => t.followed_plan === "yes");
  const deviated = trades.filter(t => t.followed_plan === "no");
  const followedWR = followed.length ? Math.round((followed.filter(t => t.pnl > 0).length / followed.length) * 100) : null;
  const deviatedWR = deviated.length ? Math.round((deviated.filter(t => t.pnl > 0).length / deviated.length) * 100) : null;

  const assetPnl = trades.reduce((acc, t) => {
    acc[t.asset] = (acc[t.asset] || 0) + (t.pnl || 0);
    return acc;
  }, {});
  const topAssets = Object.entries(assetPnl).sort((a, b) => b[1] - a[1]).slice(0, 3);

  async function getAnalysis() {
    if (!trades.length) return;
    setLoading(true);
    const summary = trades.map(t => `${t.asset} ${t.direction}: plan=${t.followed_plan}, pnl=${t.pnl}, note="${t.what_happened || ""}"`).join("\n");
    const text = await askClaude(`You are a trading coach. Analyze this trader's journal and give 3 specific pattern observations. Be direct and data-driven. Each observation 1-2 sentences. Format as numbered list.\n\nTrades:\n${summary}`);
    setAnalysis(text);
    setLoading(false);
  }

  return (
    <>
      <div className="page-title">Review</div>
      {trades.length === 0 ? (
        <div className="empty">Close some trades first to see your review.</div>
      ) : (
        <>
          <div className="review-grid">
            <div className="review-card">
              <div className="review-card-label">Win rate by adherence</div>
              {followedWR !== null && (
                <>
                  <div className="stat-row"><span className="stat-name">Followed plan</span><span className="stat-val" style={{ color: "var(--green)" }}>{followedWR}%</span></div>
                  <div className="bar-bg"><div className="bar-fill" style={{ width: `${followedWR}%`, background: "var(--green)" }} /></div>
                </>
              )}
              {deviatedWR !== null && (
                <>
                  <div className="stat-row"><span className="stat-name">Deviated</span><span className="stat-val" style={{ color: "var(--red)" }}>{deviatedWR}%</span></div>
                  <div className="bar-bg"><div className="bar-fill" style={{ width: `${deviatedWR}%`, background: "var(--red)" }} /></div>
                </>
              )}
            </div>
            <div className="review-card">
              <div className="review-card-label">P&L by asset</div>
              {topAssets.map(([asset, pnl]) => (
                <div key={asset} className="stat-row">
                  <span className="stat-name">{asset}</span>
                  <span className="stat-val" style={{ color: pnl >= 0 ? "var(--green)" : "var(--red)" }}>{fmt(pnl)}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ padding: "0 16px", marginBottom: 16 }}>
            <div className="card-sm">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: analysis ? 14 : 0 }}>
                <div className="review-card-label" style={{ margin: 0 }}>Claude's pattern analysis</div>
                <button className="btn btn-secondary" style={{ padding: "6px 14px", fontSize: 12 }} onClick={getAnalysis} disabled={loading}>
                  {loading ? "Analyzing..." : analysis ? "Refresh" : "Analyze"}
                </button>
              </div>
              {loading && <div className="loading"><div className="spinner" /> Reading your trades...</div>}
              {analysis && !loading && (
                <div className="claude-text" style={{ marginTop: 12, fontSize: 13, color: "var(--text2)", lineHeight: 1.7, whiteSpace: "pre-line" }}>{analysis}</div>
              )}
            </div>
          </div>

          <div className="section-label">All closed trades</div>
          <div className="trade-list">
            {trades.map(t => (
              <div key={t.id} className="trade-row" style={{ cursor: "default" }}>
                <div className={`dot ${(t.pnl || 0) > 0 ? "green" : "red"}`} />
                <div className="trade-asset">{t.asset}</div>
                <div className="trade-date">{fmtDate(t.created_at)}</div>
                <div className={`badge ${t.followed_plan}`}>{planLabel(t.followed_plan)}</div>
                <div className={`trade-pnl ${(t.pnl || 0) >= 0 ? "pos" : "neg"}`}>{fmt(t.pnl || 0)}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
